import type { Infer } from "convex/values";
import type { freightHandlingValidator } from "../../convex/schemas/validators/quoteEnums.lib";
import type { StripeFeeResult } from "./types";
import { CARD_PROCESSING_FEES, STRIPE_FIXED_FEE_CENTS } from "./stripe";
import {
  FULL_BASIS_POINTS,
  MARKETPLACE_COST_SHARE_BASIS_POINTS,
} from "./constants";

export function roundCents(value: number): number {
  return Math.round(value) || 0;
}

export function ceilToCents(value: number): number {
  return Math.ceil(value - 1e-6) || 0;
}

export function centsToDollars(cents: number): number {
  return roundCents(cents) / 100;
}

export function dollarsToCents(dollars: number): number {
  return roundCents(dollars * 100);
}

export function calculateBuyerPriceFromVendorPrice(
  vendorPriceCents: number,
  effectiveRate: number,
): number {
  if (vendorPriceCents <= 0) return 0;
  return ceilToCents(vendorPriceCents * (1 + effectiveRate));
}

export function marketplacePriceCents(costCents: number): number {
  if (costCents <= 0) return 0;
  return Math.ceil((costCents * FULL_BASIS_POINTS) / MARKETPLACE_COST_SHARE_BASIS_POINTS);
}

export function marketplaceFreightPriceCents(carrierCents: number): number {
  if (carrierCents <= 0) return 0;
  return marketplacePriceCents(carrierCents + STRIPE_FIXED_FEE_CENTS);
}

export function resolveApplicationFeeCents(
  buyerGrandTotalCents: number,
  sellerPayoutCents: number,
): number {
  return Math.max(0, buyerGrandTotalCents - sellerPayoutCents);
}

export function vendorTransferCents(pricing: {
  buyerGrandTotalCents: number;
  taxCents: number;
  applicationFeeAmount: number;
}): number {
  return Math.max(
    0,
    pricing.buyerGrandTotalCents - pricing.taxCents - pricing.applicationFeeAmount,
  );
}

export function splitSellerPayout(args: {
  buyerGrandTotalCents: number;
  taxCents: number;
  vendorTotalCents: number;
}): { sellerPayoutCents: number; applicationFeeAmount: number; transferAmount: number } {
  const applicationFeeAmount = resolveApplicationFeeCents(
    args.buyerGrandTotalCents - args.taxCents,
    args.vendorTotalCents,
  );
  return {
    sellerPayoutCents: args.vendorTotalCents,
    applicationFeeAmount,
    transferAmount: vendorTransferCents({ ...args, applicationFeeAmount }),
  };
}

export function transferCarriesTax(pricing: {
  buyerGrandTotalCents?: number;
  buyerLineTotalCents?: number;
  applicationFeeAmount?: number;
  transferAmount?: number;
}): boolean {
  const { buyerGrandTotalCents, buyerLineTotalCents, applicationFeeAmount, transferAmount } = pricing;
  if (
    buyerGrandTotalCents === undefined ||
    buyerLineTotalCents === undefined ||
    applicationFeeAmount === undefined ||
    transferAmount === undefined
  ) {
    return false;
  }
  return buyerGrandTotalCents > buyerLineTotalCents && transferAmount === buyerGrandTotalCents - applicationFeeAmount;
}

export function calculateMarginPercent(
  buyerTotalCents: number,
  vendorTotalCents: number,
): number {
  if (buyerTotalCents <= 0) return 0;
  const platformFee = buyerTotalCents - vendorTotalCents;
  return roundCents((platformFee / buyerTotalCents) * 100);
}

export function calculateMarkupPercent(
  buyerTotalCents: number,
  vendorTotalCents: number,
): number {
  if (vendorTotalCents <= 0) return 0;
  const platformFee = buyerTotalCents - vendorTotalCents;
  return roundCents((platformFee / vendorTotalCents) * 100);
}

export function calculateTaxCents(
  baseCents: number,
  taxRatePercent: number,
): number {
  if (baseCents <= 0 || taxRatePercent <= 0) return 0;
  return roundCents((baseCents * taxRatePercent) / 100);
}

export function calculateDiscountedAmount(
  amountCents: number,
  discountPercent: number,
): number {
  if (amountCents <= 0 || discountPercent <= 0) return amountCents;
  if (discountPercent >= 100) return 0;
  return roundCents(amountCents * (1 - discountPercent / 100));
}

export function calculateAverageCents(values: number[]): number {
  if (values.length === 0) return 0;
  const sum = values.reduce((total, value) => total + value, 0);
  return roundCents(sum / values.length);
}

export function calculatePercentDiff(
  actual: number,
  reference: number,
): number {
  if (reference === 0) return 0;
  return ((actual - reference) / Math.abs(reference)) * 100;
}

export function calculateStripeFees(
  totalCents: number,
  isInternational: boolean = false,
  isAmex: boolean = false,
): StripeFeeResult {
  const basePercentage = CARD_PROCESSING_FEES.basePercentage;
  const intlSurcharge = isInternational
    ? CARD_PROCESSING_FEES.internationalSurchargePercentage
    : 0;
  const brandSurcharge = isAmex
    ? CARD_PROCESSING_FEES.amexSurchargePercentage
    : 0;

  const effectivePercentage = basePercentage + intlSurcharge + brandSurcharge;
  const feeCents =
    totalCents > 0
      ? roundCents(totalCents * (effectivePercentage / 100)) +
        STRIPE_FIXED_FEE_CENTS
      : 0;

  return {
    feeCents,
    effectivePercentage,
    components: {
      basePercentage,
      internationalSurchargePercentage: intlSurcharge,
      brandSurchargePercentage: brandSurcharge,
    },
  };
}

export function calculateSimpleStripeFees(totalCents: number): number {
  return calculateStripeFees(totalCents).feeCents;
}

export type FreightHandling = Infer<typeof freightHandlingValidator>;

export interface AdminQuoteTotals {
  subtotalCents: number;
  freightTotalCents: number;
  taxCents: number;
  grandTotalCents: number;
  totalProfitCents: number;
  totalMarginPercent: number;
}

export function calculateBuyerPriceForMargin(
  platformCostCents: number,
  targetMarginPercent: number,
): number {
  if (platformCostCents <= 0) return 0;
  if (targetMarginPercent >= 100) return Infinity;
  if (targetMarginPercent <= 0) return platformCostCents;
  return roundCents(platformCostCents / (1 - targetMarginPercent / 100));
}

export function calculateActualMarginPercent(
  buyerPriceCents: number,
  platformCostCents: number,
): number {
  if (buyerPriceCents <= 0) return 0;
  const profitCents = buyerPriceCents - platformCostCents;
  return (profitCents / buyerPriceCents) * 100;
}

export function calculateProfitCents(
  buyerPriceCents: number,
  platformCostCents: number,
): number {
  return buyerPriceCents - platformCostCents;
}

export function calculatePlatformCostBasis(
  vendorTotalCents: number,
  freightCents: number,
  freightHandling: FreightHandling,
): number {
  return freightHandling === "included" || freightHandling === "baked_in"
    ? vendorTotalCents + freightCents
    : vendorTotalCents;
}
